import { ServiceSettings, Site, SiteListObject } from "~/entities/siteEntities";
import { isMenuOpen } from "./menuHelpers";
import { Availability, DaysOfWeek } from "~/entities/baseEntities";
import { displayTimeOfDayShort, timeFromObject } from "./dateHelper";
import { toZonedTime } from "date-fns-tz";
import { Menu } from "~/entities/menuEntities";
import { SiteWithPosition } from "~/modules/sites/components/SitesList/SitesList";
import {
  isBefore,
  isCurrentlyAvailable,
  isSameOrBefore,
} from "~/helpers/availabilityHelpers";

export const dayOfWeekByDayIndex: (keyof DaysOfWeek)[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export const daysOfTheWeekDisplayOrder: (keyof DaysOfWeek)[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const shortDayOfWeekText: Record<keyof DaysOfWeek, string> = {
  monday: "M",
  tuesday: "Tu",
  wednesday: "W",
  thursday: "Th",
  friday: "F",
  saturday: "Sa",
  sunday: "Su",
};

// Get the currently applicable availability, not the scheduled one
export const getApplicableStoreHours = (site: SiteListObject) =>
  site.openingTimes[0];

export const siteHasStoreHours = (site: SiteListObject) =>
  getApplicableStoreHours(site)?.availability?.length > 0;

export const isStoreOpen = (site: SiteListObject): boolean => {
  const localTime = toZonedTime(new Date(), site.timeZone);

  const availability =
    (site.openingTimes.length ? site.openingTimes[0].availability : []) || [];

  return isCurrentlyAvailable(availability, localTime);
};

export const getReadableHours = (
  value: Availability,
  alwaysAvailableText: string
): string => {
  const isAlwaysOpen = value.startsAt.hour === 0 && value.endsAt.hour === 24;
  return isAlwaysOpen
    ? alwaysAvailableText
    : `${timeFromObject(value.startsAt)} - ${timeFromObject(value.endsAt)}`;
};

export const getReadableHoursShort = (
  value: Availability,
  alwaysAvailableText: string
): string => {
  const isAlwaysOpen =
    value.startsAt.minute === 0 &&
    value.endsAt.minute === 0 &&
    value.startsAt.hour === 0 &&
    (value.endsAt.hour === 24 || value.endsAt.hour === 0);
  return isAlwaysOpen
    ? alwaysAvailableText
    : `${displayTimeOfDayShort(value.startsAt, true)}-${displayTimeOfDayShort(
        value.endsAt,
        true
      )}`;
};

export const getReadableDays = (days: DaysOfWeek): string =>
  daysOfTheWeekDisplayOrder
    .filter((x) => days[x])
    .map((x) => shortDayOfWeekText[x])
    .join("\xa0");

const shortDayFromDispIndex = (index: number) =>
  shortDayOfWeekText[daysOfTheWeekDisplayOrder[index]];

const displayDayRange = (range: { start: number; end: number }) => {
  if (range.start === range.end) {
    return shortDayFromDispIndex(range.start);
  } else if (range.end - range.start === 1) {
    return `${shortDayFromDispIndex(range.start)}\xa0${shortDayFromDispIndex(
      range.end
    )}`;
  } else {
    return `${shortDayFromDispIndex(range.start)}-${shortDayFromDispIndex(
      range.end
    )}`;
  }
};

export const getReadableDaysShort = (days: DaysOfWeek): string => {
  // Convert days to a boolean[] for whether available at the day of that index
  const avArray = daysOfTheWeekDisplayOrder.map((day) => days[day]);

  // If available every day show nothing
  if (avArray.every((x) => x)) {
    return "";
  }

  // Convert this array to a list of ranges, where each element lists a start index where the array values became true, along with the last value before it became false
  const ranges: { start: number; end: number }[] = [];
  for (let i = 0; i < daysOfTheWeekDisplayOrder.length; i++) {
    if (avArray[i]) {
      // If the previous range's end is the prev index, extend that range instead of inserting a new range
      if (ranges.length && ranges[ranges.length - 1].end === i - 1) {
        ranges[ranges.length - 1].end = i;
      } else {
        ranges.push({
          start: i,
          end: i,
        });
      }
    }
  }

  // Now mush the ranges together, showing only the start/end date for each (for ranges of >= 3 days)
  return ranges.map((range) => displayDayRange(range)).join(",\xa0");
};

// english readable store time
export const getReadableAvailability = (values: Availability[]): string => {
  if (values.length > 0) {
    return values
      .map((value) => getReadableHours(value, "Always open"))
      .join(", ");
  }
  return "Closed";
};

// english readable store time
export const getStoreTime = (site: SiteListObject) => {
  const currentDay = toZonedTime(new Date(), site.timeZone).getDay();
  const availability = getAvailabilityForDay(
    site,
    dayOfWeekByDayIndex[currentDay]
  );

  return getReadableAvailability(availability);
};

export const getMappedAvailability = (
  availability: Availability[]
): Record<keyof DaysOfWeek, Availability[]> => {
  const availabilityMap: Record<keyof DaysOfWeek, Availability[]> = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  const normalisedAvailability = normaliseAvailability(availability);

  daysOfTheWeekDisplayOrder.forEach((day) => {
    const times = normalisedAvailability.filter((x) => x.applies[day]);
    times.sort((a, b) => (isSameOrBefore(a.startsAt, b.startsAt) ? -1 : 1));

    const dayRes: Availability[] = [];

    for (const time of times) {
      if (
        dayRes.length > 0 &&
        isSameOrBefore(time.startsAt, dayRes[dayRes.length - 1].endsAt)
      ) {
        if (isSameOrBefore(time.endsAt, dayRes[dayRes.length - 1].endsAt)) {
          // skip because new time is completely within existing
        } else {
          dayRes[dayRes.length - 1].endsAt = time.endsAt;
        }
      } else {
        dayRes.push({
          startsAt: time.startsAt,
          endsAt: time.endsAt,
          applies: time.applies,
        });
      }
    }

    availabilityMap[day] = dayRes;
  });

  return availabilityMap;
};

export const getAvailabilityForDay = (
  site: SiteListObject,
  day: keyof DaysOfWeek
) => {
  const applicableStoreHours = getApplicableStoreHours(site);

  if (!applicableStoreHours) {
    return [];
  }
  return getMappedAvailability(applicableStoreHours.availability)[day];
};

export const normaliseAvailability = (
  availability: Availability[]
): Availability[] => {
  const res: Availability[] = [];

  availability.forEach((a) => {
    if (isBefore(a.startsAt, a.endsAt)) {
      res.push(a);
    } else {
      res.push({
        applies: a.applies,
        startsAt: a.startsAt,
        endsAt: {
          hour: 24,
          minute: 0,
        },
      });

      if (a.endsAt.hour !== 0 || a.endsAt.minute !== 0) {
        res.push({
          applies: {
            monday: a.applies.sunday,
            tuesday: a.applies.monday,
            wednesday: a.applies.tuesday,
            thursday: a.applies.wednesday,
            friday: a.applies.thursday,
            saturday: a.applies.friday,
            sunday: a.applies.saturday,
          },
          startsAt: {
            hour: 0,
            minute: 0,
          },
          endsAt: a.endsAt,
        });
      }
    }
  });

  return res;
};

export const isServiceTypeAvailable = (
  site: Site | SiteWithPosition | null | undefined,
  orderTypeCode: string
): boolean => {
  return (
    site?.serviceAvailability?.find((s) => s.orderTypeCode === orderTypeCode)
      ?.available ?? true
  );
};

export const getServiceSettings = (
  site: Site | null | undefined,
  orderTypeCode: string
): ServiceSettings | null | undefined => {
  return site?.serviceSettings?.find((s) => s.orderTypeCode === orderTypeCode);
};

export const getServiceStatus = (
  site: Site | null | undefined,
  menu: Menu | null | undefined,
  orderTypeCode: string,
  requestedReadyTime: string | null | undefined
): {
  isAvailable: boolean;
  menuOpen: boolean;
  serviceAvailable: boolean;
} => {
  const menuOpen = menu
    ? isMenuOpen(menu, requestedReadyTime, site?.timeZone)
    : false;
  const serviceAvailable = isServiceTypeAvailable(site, orderTypeCode);

  return {
    isAvailable: menuOpen && serviceAvailable,
    menuOpen,
    serviceAvailable,
  };
};

export const mapSiteToSiteListObj = (site: Site) => {
  return {
    id: site.id,
    address: site.siteAddress,
    name: site.siteName,
    image: site.siteImage,
    serviceAvailability: site.serviceAvailability,
    serviceSettings: site.serviceSettings,
    orderTypes: site.orderTypes,
    branding: site.branding,
    openingTimes: site.openingTimes,
    timeZone: site.timeZone,
  };
};
